import { parseSellerReference } from '../../../../utils/sellerReference';

export interface SellerOption {
  value: string;
  label: string;
  website?: string;
}

export const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const normalized = String(value).trim();
  return normalized && normalized !== 'null' ? normalized : null;
};

export const optionFromRecord = (
  record: Record<string, unknown>,
  fallbackLabel?: string,
  fallbackValue?: string,
): SellerOption | null => {
  const value =
    asNonEmptyString(record.seller_lvl1) ||
    asNonEmptyString(record.seller_id) ||
    asNonEmptyString(record.lvl1_id) ||
    asNonEmptyString(record.id) ||
    asNonEmptyString(record.$id) ||
    asNonEmptyString(record.value) ||
    fallbackValue ||
    null;

  if (!value) return null;

  const parsedReference = parseSellerReference(value);

  const website =
    asNonEmptyString(record.website_url) ||
    asNonEmptyString(record.website) ||
    asNonEmptyString(record.company_website) ||
    undefined;
  const label =
    asNonEmptyString(record.company_name) ||
    asNonEmptyString(record.seller_name) ||
    asNonEmptyString(record.name) ||
    asNonEmptyString(record.label) ||
    parsedReference?.name ||
    website ||
    fallbackLabel ||
    value;

  return { value, label, website };
};

const parseSellerIds = (sellerIds: unknown): unknown => {
  if (typeof sellerIds !== 'string') return sellerIds;

  const trimmed = sellerIds.trim();
  if (!trimmed) return [];

  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed.includes(',')
      ? trimmed.split(',').map((entry) => entry.trim()).filter(Boolean)
      : trimmed;
  }
};

export const normalizeSellerOptions = (sellerIds: unknown): SellerOption[] => {
  const parsed = parseSellerIds(sellerIds);
  const options: SellerOption[] = [];

  const collect = (value: unknown) => {
    if (value === null || value === undefined || value === '') return;

    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }

    const primitive = asNonEmptyString(value);
    if (primitive) {
      const parsedReference = parseSellerReference(primitive);
      options.push({ value: primitive, label: parsedReference?.name || primitive });
      return;
    }

    if (typeof value !== 'object') return;
    const record = value as Record<string, unknown>;
    const directOption = optionFromRecord(record);
    if (directOption) {
      options.push(directOption);
      return;
    }

    const nestedSellers = record.seller_ids ?? record.sellers;
    if (nestedSellers !== undefined) {
      collect(parseSellerIds(nestedSellers));
      return;
    }

    Object.entries(record).forEach(([key, entry]) => {
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        const mappedOption = optionFromRecord(
          entry as Record<string, unknown>,
          key,
          key,
        );
        if (mappedOption) options.push(mappedOption);
        return;
      }

      const mappedLabel = asNonEmptyString(entry);
      if (mappedLabel) options.push({ value: key, label: mappedLabel });
    });
  };

  collect(parsed);

  return Array.from(new Map(options.map((option) => [option.value, option])).values());
};
